import { Query, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { EmployeeSummary, Timeframe } from "@performyard/service-contracts";

import { employeeSummaryQuery } from "../../../api/queries/defs/employee-summary";
import { regenerateEmployeeSummary } from "../../../api/services/defs/internal-api";

import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const EMPLOYEE_SUMMARY_POLL_INTERVAL_MS = 4000;

export const useEmployeeSummary = (
	userId: string,
	timeframe: Timeframe,
	options?: PYUseQueryOptions<EmployeeSummary>
) =>
	useQuery<EmployeeSummary>({
		...employeeSummaryQuery(userId, timeframe),
		...options,
		enabled: !!userId && (options?.enabled ?? true),
		refetchInterval: (query: Query<EmployeeSummary>) =>
			query.state.data?.generation?.status === "in_progress" ? EMPLOYEE_SUMMARY_POLL_INTERVAL_MS : false,
	});

export const useRegenerateEmployeeSummary = (
	userId: string,
	options?: PYUseMutationOptions<EmployeeSummary, unknown, Timeframe>
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["EmployeeSummaryRegenerate", userId],
		mutationFn: (timeframe: Timeframe) => regenerateEmployeeSummary(userId, timeframe),
		...options,
		onSuccess: (data, timeframe, context) => {
			queryClient.setQueryData(["EmployeeSummary", userId, timeframe], data);
			if (options?.onSuccess) options.onSuccess(data, timeframe, context);
		},
		meta: {
			errorMessage: "There was an error regenerating employee insights.",
		},
	});
};
